/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button,
} from '@one-thd/sui-atomic-components';
import { useDraggableDrawer } from '../../../../hooks/useDraggableDrawer';
import { DRAWER_HEIGHTS } from '../../constants';

/**
 * A draggable drawer.
 *
 * @param { DraggableDrawerProps } props - The properties for the DraggableDrawer component.
 * - `children`: Content to display inside the drawer.
 * - `drawerState`: The state of the mobile drawer.
 * - `setDrawerState`: Function to update drawerState.
 * - `onClose`: Function to execute when the drawer is closed.
 * @param { React.ForwardedRef<HTMLDivElement> } ref - Reference to the drawer element.
 *
 * @returns {JSX.Element} The DraggableDrawer component.
 */

const DraggableDrawer = ({
  children,
  drawerState,
  setDrawerState,
}) => {
  const [currentHeight, setCurrentHeight] = useState(DRAWER_HEIGHTS[drawerState]);
  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dynamicHeight,
    dynamicBottom,
  } = useDraggableDrawer(drawerState, setDrawerState, currentHeight);
  const triggerRef = useRef(null);
  const [isAssistantReady, setIsAssistantReady] = useState(false);

  useEffect(() => {
    const handleAssistantReady = () => setIsAssistantReady(true);

    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('magic-apron-assistant.isReady', handleAssistantReady);
    }

    return () => {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off('magic-apron-assistant.isReady', handleAssistantReady);
    };
  }, []);

  const handleBarLabel = drawerState === 'OPEN' ? 'Drag down to minimize' : 'Drag to open or close';

  const containerClasses = classNames(
    'drawer-container sui-flex sui-flex-col sui-overflow-y-hidden sui-bg-primary sui-z-max sui-outline-none sui-max-w-full sui-w-full sui-rounded-t-lg sui-bottom-0 sui-left-0 sui-transition-[height,bottom] sui-duration-500',
    drawerState === 'OPEN' ? 'sui-fixed' : 'sui-absolute',
    {
      'sui-shadow-[0_-1px_6px_2px_rgba(0,0,0,0.3)]': drawerState !== 'CLOSED',
      'sui-hidden': !isAssistantReady && drawerState === 'MINIMIZED'
    }
  );

  useEffect(() => {
    setCurrentHeight(DRAWER_HEIGHTS[drawerState]);
  }, [drawerState]);

  useEffect(() => {
    const handleInteraction = (event) => {
      if (triggerRef.current && triggerRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('touchstart', handleInteraction, { passive: false });
    }

    return (() => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('touchstart', handleInteraction);
      }
    });
  }, []);

  return (
    <div
      className={containerClasses}
      style={{ height: dynamicHeight, bottom: dynamicBottom }}
    >
      <Button
        className="drawer-header-with-handlebar-div sui-bg-primary sui-sticky sui-top-0 sui-bottom-0 sui-z-200 sui-border-none"
        onTouchStart={handlePointerDown}
        onMouseDown={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        onMouseUp={handlePointerUp}
        ref={triggerRef}
        aria-label={handleBarLabel}
        role="button"
      >
        <div
          className="sui-w-16 sui-h-1 sui-bg-strong sui-rounded-full sui-mt-2 sui-mb-4 sui-block sui-mx-auto"
        >
          <span className="sui-sr-only">{handleBarLabel}</span>
        </div>
      </Button>
      {children}
    </div>
  );
};

DraggableDrawer.displayName = 'DraggableDrawer';

DraggableDrawer.propTypes = {
  children: PropTypes.node.isRequired,
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: PropTypes.func.isRequired,
};

export { DraggableDrawer };
